import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Progress } from "../ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import {
    Search,
    RefreshCw,
    AlertTriangle,
    Users,
    Phone,
    Mail,
    Calendar,
    Building2,
    User,
    CheckCircle,
    XCircle,
    Trash2,
    Filter
} from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { toast } from "sonner";
import { runDuplicateCheck, clearDuplicateFlags, deleteDuplicateLeads, fetchDistinctValues, deleteLead } from "../../services/crmServices/leadService";
import { getFullName } from "../../services/common/util";
import { format } from "date-fns";

export function DuplicateCheckModal({ open, onClose, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duplicateGroups, setDuplicateGroups] = useState([]);
    const [checking, setChecking] = useState(false);
    const [checkCompleted, setCheckCompleted] = useState(false);
    const [selectedSource, setSelectedSource] = useState("");
    const [availableSources, setAvailableSources] = useState([]);
    const [loadingSources, setLoadingSources] = useState(false);
    const [deletingLeadId, setDeletingLeadId] = useState(null);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [statusCounts, setStatusCounts] = useState({});
    // Track which duplicate leads are selected for deletion
    const [selectedDuplicateIds, setSelectedDuplicateIds] = useState(new Set());
    const [stats, setStats] = useState({
        totalLeads: 0,
        duplicateLeads: 0,
        duplicateGroups: 0,
        uniquePhones: 0,
        hasMore: false,
        totalGroups: 0,
        displayedGroups: 0
    });

    useEffect(() => {
        if (open) {
            init();
            loadSources();
        }
    }, [open]);

    const loadSources = async () => {
        setLoadingSources(true);
        try {
            const response = await fetchDistinctValues("source");
            if (response?.data) {
                setAvailableSources(response.data.map(source => ({
                    label: source,
                    value: source
                })));
            }
        } catch (error) {
            console.error("Error loading sources:", error);
        } finally {
            setLoadingSources(false);
        }
    };

    const init = async () => {
        setDuplicateGroups([]);
        setStats({});
        setCheckCompleted(false);
        setSelectedSource("");
        setSelectedStatuses([]);
        setStatusCounts({});
        setSelectedDuplicateIds(new Set());
    };

    const handleRunDuplicateCheck = async () => {
        setChecking(true);
        setProgress(0);

        try {
            // Simulate progress updates
            const progressInterval = setInterval(() => {
                setProgress((prev) => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return prev;
                    }
                    return prev + 10;
                });
            }, 500);

            const response = await runDuplicateCheck(selectedSource || undefined);

            clearInterval(progressInterval);
            setProgress(100);

            if (response?.status === 200) {
                setDuplicateGroups(response.data.groups || []);
                setStats(response.data.stats || {});
                setCheckCompleted(true);
                
                // Calculate status counts (only for duplicates, not originals)
                const counts = {};
                response.data.groups.forEach(group => {
                    group.leads.slice(1).forEach(lead => { // Skip first lead (original)
                        const status = lead.status || "Unknown";
                        counts[status] = (counts[status] || 0) + 1;
                    });
                });
                setStatusCounts(counts);
                setSelectedStatuses([]); // Reset filter

                const hasMore = response.data.hasMore || false;
                const displayedGroups = response.data.displayedGroups || response.data.groups.length;
                const totalGroups = response.data.totalGroups || response.data.groups.length;
                
                if (hasMore) {
                    toast.warning(
                        `Showing first ${displayedGroups} groups (${response.data.stats.duplicateLeads} duplicates). ` +
                        `Total groups: ${totalGroups}. Use status filter to narrow results.`,
                        { duration: 8000 }
                    );
                } else {
                    toast.success(
                        `Duplicate check completed! Found ${response.data.stats.duplicateLeads} duplicates in ${response.data.stats.duplicateGroups} groups.`
                    );
                }
            } else {
                toast.error("Failed to run duplicate check");
            }
        } catch (error) {
            console.error("Error running duplicate check:", error);
            toast.error("Failed to run duplicate check");
        } finally {
            setChecking(false);
            setProgress(0);
        }
    };

    const handleClearDuplicateFlags = async () => {
        try {
            const response = await clearDuplicateFlags();

            if (response?.status === 200) {
                setDuplicateGroups([]);
                setStats({});
                setCheckCompleted(false);
                toast.success("Duplicate flags cleared successfully");
                onSuccess && onSuccess();
            } else {
                toast.error("Failed to clear duplicate flags");
            }
        } catch (error) {
            console.error("Error clearing duplicate flags:", error);
            toast.error("Failed to clear duplicate flags");
        }
    };

    const handleDeleteSelectedDuplicates = async () => {
        const selectedIds = Array.from(selectedDuplicateIds);
        
        if (selectedIds.length === 0) {
            toast.error("Please select at least one duplicate lead to delete");
            return;
        }

        if (
            !window.confirm(
                `Are you sure you want to delete ${selectedIds.length} selected duplicate lead(s)? This action cannot be undone.`
            )
        ) {
            return;
        }

        setLoading(true);
        let deletedCount = 0;
        let errorCount = 0;
        const totalLeads = selectedIds.length;

        // Show progress toast
        const progressToast = toast.loading(`Deleting ${totalLeads} duplicate leads... 0 / ${totalLeads} (0%)`);

        try {
            // Delete selected leads one by one with progress tracking
            for (let i = 0; i < selectedIds.length; i++) {
                const leadId = selectedIds[i];
                try {
                    const response = await deleteLead({ _id: leadId });
                    if (response?.status === 200 || response?.data) {
                        deletedCount++;
                    } else {
                        errorCount++;
                    }
                } catch (error) {
                    console.error(`Error deleting lead ${leadId}:`, error);
                    errorCount++;
                }

                // Update progress toast
                const processed = i + 1;
                const percentage = Math.round((processed / totalLeads) * 100);
                toast.loading(
                    `Deleting ${totalLeads} duplicate leads... ${processed} / ${totalLeads} (${percentage}%)`,
                    { id: progressToast }
                );
            }

            toast.dismiss(progressToast);

            if (deletedCount > 0) {
                toast.success(`Successfully deleted ${deletedCount} duplicate lead(s)!`);
                if (errorCount > 0) {
                    toast.warning(`${errorCount} lead(s) could not be deleted`);
                }
                setSelectedDuplicateIds(new Set());
                await init();
                // Re-run duplicate check to refresh the list
                if (checkCompleted) {
                    await handleRunDuplicateCheck();
                }
            } else {
                toast.error("Failed to delete duplicate leads");
            }
        } catch (error) {
            toast.dismiss(progressToast);
            console.error("Error deleting duplicate leads:", error);
            toast.error("Failed to delete duplicate leads");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAllDuplicates = async () => {
        // Collect all duplicate lead IDs (excluding the first lead in each group)
        const allDuplicateIds = duplicateGroups.flatMap((group) => group.leads.slice(1).map((lead) => lead._id));

        if (allDuplicateIds.length === 0) {
            toast.info("No duplicate leads to delete");
            return;
        }

        if (
            !window.confirm(
                `Are you sure you want to delete ALL ${allDuplicateIds.length} duplicate leads? This action cannot be undone. ` +
                    "Only duplicate leads will be deleted, keeping the original (first created) lead in each group."
            )
        ) {
            return;
        }

        // Set all duplicates as selected and delete them
        setSelectedDuplicateIds(new Set(allDuplicateIds));
        await handleDeleteSelectedDuplicates();
    };

    const handleDeleteFilteredDuplicates = async () => {
        const filteredGroups = getFilteredGroups();
        const filteredDuplicateIds = filteredGroups.flatMap((group) => group.leads.slice(1).map((lead) => lead._id));
        
        if (filteredDuplicateIds.length === 0) {
            toast.info("No filtered duplicate leads to delete");
            return;
        }

        if (
            !window.confirm(
                `Are you sure you want to delete ${filteredDuplicateIds.length} filtered duplicate leads?\n\n` +
                    `Statuses: ${selectedStatuses.join(", ")}\n\n` +
                    `This action cannot be undone. Only duplicate leads will be deleted, keeping the original (first created) lead in each group.`
            )
        ) {
            return;
        }

        // Set filtered duplicates as selected and delete them
        setSelectedDuplicateIds(new Set(filteredDuplicateIds));
        await handleDeleteSelectedDuplicates();
    };

    const handleToggleDuplicateSelection = (leadId) => {
        setSelectedDuplicateIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(leadId)) {
                newSet.delete(leadId);
            } else {
                newSet.add(leadId);
            }
            return newSet;
        });
    };

    const handleSelectAllDuplicates = () => {
        const allDuplicateIds = getFilteredGroups().flatMap((group) => group.leads.slice(1).map((lead) => lead._id));
        const allSelected = allDuplicateIds.length > 0 && allDuplicateIds.every(id => selectedDuplicateIds.has(id));
        
        if (allSelected) {
            // Deselect all
            setSelectedDuplicateIds(new Set());
        } else {
            // Select all
            setSelectedDuplicateIds(new Set(allDuplicateIds));
        }
    };

    const getFilteredGroups = () => {
        // If no status filter selected, show all groups
        if (selectedStatuses.length === 0) {
            return duplicateGroups;
        }
        
        // Filter groups to only show those with duplicates matching selected statuses
        return duplicateGroups
            .map(group => ({
                ...group,
                leads: [
                    group.leads[0], // Always include original (first lead)
                    ...group.leads.slice(1).filter(lead => {
                        // Include duplicates that match selected statuses
                        const leadStatus = lead.status || "Unknown";
                        return selectedStatuses.includes(leadStatus);
                    })
                ]
            }))
            .filter(group => group.leads.length > 1); // Only show groups that have filtered duplicates
    };

    const toggleStatus = (status) => {
        setSelectedStatuses(prev => 
            prev.includes(status) 
                ? prev.filter(s => s !== status)
                : [...prev, status]
        );
    };

    const getFilteredDuplicateCount = () => {
        const filteredGroups = getFilteredGroups();
        return filteredGroups.reduce((count, group) => count + (group.leads.length - 1), 0);
    };

    const handleDeleteIndividualLead = async (leadId, leadName, isOriginal) => {
        const leadType = isOriginal ? "original" : "duplicate";
        if (
            !window.confirm(
                `Are you sure you want to delete this ${leadType} lead?\n\n` +
                    `Lead: ${leadName}\n` +
                    `This action cannot be undone.`
            )
        ) {
            return;
        }

        setDeletingLeadId(leadId);
        try {
            const response = await deleteLead({ _id: leadId });

            if (response?.status === 200 || response?.data) {
                toast.success(`Successfully deleted lead`);
                
                // Remove the deleted lead from the duplicate groups and update stats
                setDuplicateGroups((prevGroups) => {
                    const updatedGroups = prevGroups
                        .map((group) => ({
                            ...group,
                            leads: group.leads.filter((lead) => lead._id !== leadId)
                        }))
                        .filter((group) => group.leads.length > 1); // Remove groups with only one lead left
                    
                    return updatedGroups;
                });

                // Update stats
                setStats((prevStats) => ({
                    ...prevStats,
                    duplicateLeads: Math.max(0, prevStats.duplicateLeads - 1)
                }));

                // Re-run duplicate check to refresh the list and get accurate stats
                if (checkCompleted) {
                    setTimeout(async () => {
                        await handleRunDuplicateCheck();
                    }, 500); // Small delay to ensure deletion is processed
                }
            } else {
                toast.error("Failed to delete lead");
            }
        } catch (error) {
            console.error("Error deleting lead:", error);
            toast.error("Failed to delete lead");
        } finally {
            setDeletingLeadId(null);
        }
    };

    const getDuplicateTypeColor = (phone) => {
        // Color code based on phone number hash for consistent grouping
        const hash = phone.split("").reduce((a, b) => {
            a = (a << 5) - a + b.charCodeAt(0);
            return a & a;
        }, 0);

        const colors = [
            "bg-red-100 text-red-800",
            "bg-blue-100 text-blue-800",
            "bg-green-100 text-green-800",
            "bg-yellow-100 text-yellow-800",
            "bg-purple-100 text-purple-800",
            "bg-pink-100 text-pink-800",
            "bg-indigo-100 text-indigo-800"
        ];

        return colors[Math.abs(hash) % colors.length];
    };

    const handleClose = () => {
        init(); // Clear duplicates data when modal closes
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center text-xl">
                        <Search className="h-6 w-6 mr-2 text-orange-600" />
                        Manual Duplicate Check
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Stats Overview */}
                    {checkCompleted && (
                        <>
                            {stats.hasMore && (
                                <Card className="border-orange-300 bg-orange-50">
                                    <CardContent className="p-4">
                                        <div className="flex items-start space-x-2">
                                            <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                                            <div className="text-sm">
                                                <p className="font-semibold text-orange-900 mb-1">
                                                    Large Duplicate Set Detected
                                                </p>
                                                <p className="text-orange-800">
                                                    Showing first <strong>{stats.displayedGroups || 500} groups</strong> out of{" "}
                                                    <strong>{stats.totalGroups || 0} total groups</strong> (performance limit).
                                                </p>
                                                <p className="text-orange-800 mt-2">
                                                    💡 <strong>Tip:</strong> Use the <strong>Status Filter</strong> below to narrow down and delete specific statuses efficiently!
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                            
                            {stats.sourceFilter && (
                                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                    <p className="text-sm text-blue-800">
                                        <strong>Filter Active:</strong> Showing duplicates for source &quot;{stats.sourceFilter}&quot; against whole CRM
                                    </p>
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <Card>
                                    <CardContent className="p-4">
                                        <div className="flex items-center space-x-2">
                                            <Users className="h-5 w-5 text-blue-600" />
                                            <div>
                                                <p className="text-sm text-gray-600">
                                                    {stats.sourceFilter ? "Leads from Source" : "Total Leads"}
                                                </p>
                                                <p className="text-2xl font-bold">{stats.totalLeads || 0}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center space-x-2">
                                        <AlertTriangle className="h-5 w-5 text-red-600" />
                                        <div>
                                            <p className="text-sm text-gray-600">Duplicate Leads</p>
                                            <p className="text-2xl font-bold text-red-600">
                                                {stats.duplicateLeads || 0}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center space-x-2">
                                        <Phone className="h-5 w-5 text-orange-600" />
                                        <div>
                                            <p className="text-sm text-gray-600">Duplicate Groups</p>
                                            <p className="text-2xl font-bold text-orange-600">
                                                {stats.duplicateGroups || 0}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center space-x-2">
                                        <CheckCircle className="h-5 w-5 text-green-600" />
                                        <div>
                                            <p className="text-sm text-gray-600">Unique Phones</p>
                                            <p className="text-2xl font-bold text-green-600">
                                                {stats.uniquePhones || 0}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            </div>

                            {/* Status Breakdown */}
                            {Object.keys(statusCounts).length > 0 && (
                                <Card className="border-blue-200 bg-blue-50">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base flex items-center">
                                            <Filter className="h-4 w-4 mr-2" />
                                            Status Breakdown
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                            {Object.entries(statusCounts)
                                                .sort((a, b) => b[1] - a[1])
                                                .map(([status, count]) => (
                                                    <div key={status} className="flex justify-between bg-white p-2 rounded">
                                                        <span className="text-gray-700">{status}:</span>
                                                        <span className="font-semibold">{count}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </>
                    )}

                    {/* Progress Bar */}
                    {checking && (
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex items-center space-x-4">
                                    <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium mb-2">Running duplicate check...</p>
                                        <Progress value={progress} className="w-full" />
                                        <p className="text-xs text-gray-500 mt-1">{progress}% complete</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Source Filter */}
                    <div className="space-y-3">
                        <Label>Filter by Source (Optional)</Label>
                        <Select
                            value={selectedSource}
                            onChange={setSelectedSource}
                            options={[
                                { label: "All Sources", value: "" },
                                ...availableSources
                            ]}
                            placeholder={loadingSources ? "Loading sources..." : "Select source to check against whole CRM"}
                            searchable={true}
                            searchPlaceholder="Search sources..."
                            disabled={checking || loading || loadingSources}
                        />
                        {selectedSource && (
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <p className="text-sm text-blue-800">
                                    <strong>Note:</strong> Duplicate check will find duplicates for leads from source &quot;{selectedSource}&quot; against the whole CRM.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Status Filter (shown after check completed) */}
                    {checkCompleted && Object.keys(statusCounts).length > 0 && (
                        <Card className="border-purple-200">
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center text-base">
                                    <Filter className="h-5 w-5 mr-2 text-purple-600" />
                                    Filter by Status (Select statuses to delete)
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {Object.entries(statusCounts)
                                        .sort((a, b) => b[1] - a[1]) // Sort by count descending
                                        .map(([status, count]) => (
                                            <div
                                                key={status}
                                                className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50"
                                            >
                                                <Checkbox
                                                    id={`status-${status}`}
                                                    checked={selectedStatuses.includes(status)}
                                                    onCheckedChange={() => toggleStatus(status)}
                                                />
                                                <Label 
                                                    htmlFor={`status-${status}`}
                                                    className="cursor-pointer flex-1"
                                                >
                                                    {status}
                                                    <Badge variant="secondary" className="ml-2 text-xs">
                                                        {count}
                                                    </Badge>
                                                </Label>
                                            </div>
                                        ))}
                                </div>
                                
                                {selectedStatuses.length > 0 && (
                                    <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                        <p className="text-sm text-purple-800">
                                            <strong>Filtered:</strong> {getFilteredDuplicateCount()} duplicates with status:{" "}
                                            {selectedStatuses.join(", ")}
                                        </p>
                                        <Button
                                            onClick={() => setSelectedStatuses([])}
                                            variant="ghost"
                                            size="sm"
                                            className="mt-2 h-7 text-xs"
                                        >
                                            Clear Status Filter
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                        <Button
                            onClick={handleRunDuplicateCheck}
                            disabled={checking || loading}
                            className=""
                        >
                            <Search className="h-4 w-4 mr-2" />
                            {checking ? "Checking..." : "Run Duplicate Check"}
                        </Button>

                        {checkCompleted && (
                            <>
                                {/* Delete Selected Duplicates */}
                                {selectedDuplicateIds.size > 0 && (
                                    <Button
                                        onClick={handleDeleteSelectedDuplicates}
                                        className=" text-white"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Trash2 className="h-4 w-4 mr-2" />
                                        )}
                                        {loading ? "Deleting..." : `Delete Selected (${selectedDuplicateIds.size})`}
                                    </Button>
                                )}

                                {/* Delete All Duplicates (no filter) */}
                                {selectedStatuses.length === 0 && (
                                    <Button
                                        onClick={handleDeleteAllDuplicates}
                                        variant="outline"
                                        className="text-red-600 border-red-600 hover:bg-red-50"
                                        disabled={
                                            loading ||
                                            duplicateGroups.length === 0 ||
                                            duplicateGroups.every((group) => group.leads.length <= 1)
                                        }
                                    >
                                        {loading ? (
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Trash2 className="h-4 w-4 mr-2" />
                                        )}
                                        {loading ? "Deleting..." : `Delete All Duplicates (${stats.duplicateLeads || 0})`}
                                    </Button>
                                )}

                                {/* Delete Filtered Duplicates */}
                                {selectedStatuses.length > 0 && (
                                    <Button
                                        onClick={handleDeleteFilteredDuplicates}
                                        variant="outline"
                                        className="text-red-600 border-red-600 hover:bg-red-50"
                                        disabled={loading || getFilteredDuplicateCount() === 0}
                                    >
                                        {loading ? (
                                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Trash2 className="h-4 w-4 mr-2" />
                                        )}
                                        {loading ? "Deleting..." : `Delete Filtered (${getFilteredDuplicateCount()})`}
                                    </Button>
                                )}
                            </>
                        )}

                        {/* <Button onClick={init} variant="outline" disabled={loading}>
                            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                            Refresh
                        </Button> */}
                    </div>

                    {/* Duplicate Groups */}
                    {duplicateGroups.length > 0 && (
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold flex items-center justify-between">
                                <div className="flex items-center">
                                    <AlertTriangle className="h-5 w-5 mr-2 text-red-600" />
                                    {selectedStatuses.length > 0 ? (
                                        <span>Filtered Duplicate Groups ({getFilteredGroups().length})</span>
                                    ) : (
                                        <span>Duplicate Groups ({duplicateGroups.length})</span>
                                    )}
                                </div>
                                <div className="flex items-center space-x-2">
                                    {selectedDuplicateIds.size > 0 && (
                                        <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                                            {selectedDuplicateIds.size} selected
                                        </Badge>
                                    )}
                                    {selectedStatuses.length > 0 && (
                                        <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                                            Showing {getFilteredDuplicateCount()} filtered duplicates
                                        </Badge>
                                    )}
                                </div>
                            </h3>

                            {getFilteredGroups().map((group, groupIndex) => (
                                <Card key={groupIndex} className="border-l-4 border-l-red-500">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="flex items-center justify-between text-base">
                                            <div className="flex items-center space-x-2">
                                                <Phone className="h-4 w-4 text-red-600" />
                                                <span>
                                                    {group.phoneType === "secondary" ? "Second Phone:" : "Phone:"}{" "}
                                                    {group.phone}
                                                </span>
                                                <Badge className={getDuplicateTypeColor(group.phone)}>
                                                    {group.leads.length} Duplicates
                                                </Badge>
                                            </div>
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>
                                                        <div className="flex items-center space-x-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={(() => {
                                                                    const allDuplicateIds = getFilteredGroups().flatMap((group) => group.leads.slice(1).map((lead) => lead._id));
                                                                    return allDuplicateIds.length > 0 && allDuplicateIds.every(id => selectedDuplicateIds.has(id));
                                                                })()}
                                                                onChange={handleSelectAllDuplicates}
                                                                className="rounded"
                                                            />
                                                            <span>Select</span>
                                                        </div>
                                                    </TableHead>
                                                    <TableHead>Lead ID</TableHead>
                                                    <TableHead>Name</TableHead>
                                                    <TableHead>Email</TableHead>
                                                    <TableHead>Status</TableHead>
                                                    <TableHead>Source</TableHead>
                                                    <TableHead>Desk</TableHead>
                                                    <TableHead>Agent</TableHead>
                                                    <TableHead>Created</TableHead>
                                                    <TableHead>Type</TableHead>
                                                    <TableHead>Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {group.leads.map((lead, leadIndex) => (
                                                    <TableRow
                                                        key={lead._id}
                                                        className={leadIndex === 0 ? "bg-green-50" : "bg-red-50"}
                                                    >
                                                        <TableCell>
                                                            {leadIndex === 0 ? (
                                                                <span className="text-gray-400">-</span>
                                                            ) : (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedDuplicateIds.has(lead._id)}
                                                                    onChange={() => handleToggleDuplicateSelection(lead._id)}
                                                                    className="rounded"
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="font-mono text-xs">{lead._id}</TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center space-x-2">
                                                                <User className="h-4 w-4 text-gray-500" />
                                                                <span>{getFullName(lead)}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center space-x-2">
                                                                <Mail className="h-4 w-4 text-gray-500" />
                                                                <span className="truncate max-w-40">
                                                                    {lead.email || "-"}
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant="secondary">{lead.status}</Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant="outline" className="text-xs">
                                                                {lead.source || "-"}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center space-x-2">
                                                                <Building2 className="h-4 w-4 text-gray-500" />
                                                                <span>{lead.desk?.name || "-"}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>{getFullName(lead.agent) || "-"}</TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center space-x-2">
                                                                <Calendar className="h-4 w-4 text-gray-500" />
                                                                <span className="text-sm">
                                                                    {format(new Date(lead.createdAt), "MMM dd, yyyy")}
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            {leadIndex === 0 ? (
                                                                <Badge className="bg-green-100 text-green-800">
                                                                    <CheckCircle className="h-3 w-3 mr-1" />
                                                                    Original
                                                                </Badge>
                                                            ) : (
                                                                <Badge className="bg-red-100 text-red-800">
                                                                    <XCircle className="h-3 w-3 mr-1" />
                                                                    Duplicate
                                                                </Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => handleDeleteIndividualLead(
                                                                    lead._id,
                                                                    getFullName(lead),
                                                                    leadIndex === 0
                                                                )}
                                                                disabled={loading || deletingLeadId === lead._id}
                                                                className="text-red-600 border-red-600 hover:bg-red-50"
                                                            >
                                                                {deletingLeadId === lead._id ? (
                                                                    <RefreshCw className="h-3 w-3 animate-spin" />
                                                                ) : (
                                                                    <Trash2 className="h-3 w-3" />
                                                                )}
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}

                    {/* No Duplicates Found */}
                    {checkCompleted && duplicateGroups.length === 0 && (
                        <Card className="text-center py-8">
                            <CardContent>
                                <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Duplicates Found</h3>
                                <p className="text-gray-600">All leads have unique phone numbers.</p>
                            </CardContent>
                        </Card>
                    )}

                    {/* Information Note */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <div className="flex items-start space-x-2">
                            <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
                            <div className="text-sm">
                                <p className="font-medium text-blue-900 mb-1">Important Notes:</p>
                                <ul className="list-disc list-inside text-blue-800 space-y-1">
                                    <li>
                                        Duplicates are identified based on matching phone numbers (primary and
                                        secondary)
                                    </li>
                                    <li>
                                        The first created lead is marked as &ldquo;Original&rdquo;, others as
                                        &ldquo;Duplicate&rdquo;
                                    </li>
                                    <li>Duplicate leads are not automatically removed or merged</li>
                                    <li>Multiple agents may be working with the same customer legitimately</li>
                                    <li>Review each group carefully before taking any action</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                    <Button variant="outline" onClick={handleClose}>
                        Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
